<script setup lang="ts">
import { SharedData } from '@/@types'
import { Coupon } from '@/@types/cart'
import { Order, OrdersnapShot } from '@/@types/order'
import { PaymentHeader, PaymentTitle } from '@/components/shared/checkout/payment-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { formatPrice } from '@/helpers/current-price'
import { formatDateFull } from '@/helpers/format-date'
import { cn } from '@/lib/utils'
import { Link, usePage } from '@inertiajs/vue3'
import { Check } from 'lucide-vue-next'
import { ref } from 'vue'

import Resume from '../cart/payment-methods/Resume.vue'
import PaymentStepper from './payment-header/PaymentStepper.vue'

interface OrderWithCoupon extends Order {
    coupon: Coupon
    order_snap_shot: OrdersnapShot
}

const { order } = defineProps<{ order: OrderWithCoupon }>()

const { user } = usePage<SharedData>().props

const barcode = ref<HTMLDivElement | null>(null)
const copied = ref(false)

async function handleCopy() {
    await navigator.clipboard.writeText(barcode.value?.innerText ?? '')

    copied.value = true

    setTimeout(() => {
        copied.value = false
    }, 2000)
}
</script>

<template>
    <main class="container">
        <PaymentHeader :variant="order.status">
            <PaymentTitle :variant="order.status">
                {{ $t('Awaiting payment') }}
                <template #description>
                    {{ $t('Your order') }}
                    <strong> #{{ order.id }} </strong>
                    {{ $t('was placed successfully') }}
                    {{ $t('and the invoice has already been issued for payment.') }}
                </template>
            </PaymentTitle>
            <PaymentStepper
                variant="waiting"
                :steps="[
                    {
                        name: $t('Purchase made'),
                        state: 'completed',
                    },
                    {
                        name: $t('Bill payment'),
                        state: 'active',
                    },
                    {
                        name: $t('Payment received'),
                        state: 'inactive',
                    },
                ]"
            />
        </PaymentHeader>

        <section class="mt-6 flex flex-col gap-8 lg:flex-row">
            <div class="flex flex-1 flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-8">
                <div class="mx-auto flex max-w-xl flex-col items-center justify-center">
                    <Badge
                        :variant="order.status"
                        class="rounded-sm border-0 before:size-1.5 before:rounded-full before:bg-yellow-400 before:content-['']"
                    >
                        {{ $t(order.status) }}
                    </Badge>

                    <h2 class="mt-4 mb-2.5 text-center font-sans text-2xl font-semibold" v-if="order.transaction_detail">
                        {{ $t('Amount to pay:', { amount: formatPrice(order.transaction_detail.amount / 100) }) }}
                    </h2>

                    <p
                        class="mb-8 text-center text-[#444]"
                        v-html="
                            $t(`Once payment is approved, you will receive an email at  with information about the tickets purchased.`, {
                                email: user?.email,
                            })
                        "
                    />

                    <p class="text-base font-semibold" v-if="order.transaction_detail">
                        {{ $t('Pay the bill by', { date: formatDateFull(order.transaction_detail.due_date, $i18n.locale) }) }}
                    </p>
                </div>

                <Separator class="mx-auto my-8 max-w-[612px]" />

                <div class="mb-8 w-full rounded-sm bg-gray-50 p-4 text-sm">
                    <div ref="barcode" class="text-center text-base break-all" v-if="order.transaction_detail">
                        {{ order.transaction_detail.barcode }}
                    </div>

                    <div class="mt-8 flex justify-around">
                        <Button
                            :as="Link"
                            href="?show_order_details=true"
                            variant="outline"
                            class="border-primary text-primary hover:text-primary/80 min-w-3xs text-base hover:bg-transparent"
                            >{{ $t('View order details') }}</Button
                        >
                        <a :href="order.transaction_detail.pdf_url" target="_blank" v-if="order.transaction_detail">
                            <Button
                                variant="outline"
                                class="border-primary text-primary hover:text-primary/80 min-w-3xs text-base hover:bg-transparent"
                            >
                                {{ $t('View bill') }}
                            </Button>
                        </a>
                        <Button @click="handleCopy" :class="cn('relative min-w-3xs text-base', copied ? 'bg-emerald-500 hover:bg-emerald-500' : '')">
                            <div :class="cn('transition-all', copied ? 'scale-100 opacity-100' : 'scale-0 opacity-0')" aria-label="copied">
                                <Check :size="24" :stroke-width="2" aria-hidden="true" />
                            </div>
                            <div :class="cn('absolute transition-all', copied ? 'scale-0 opacity-0' : 'scale-100 opacity-100')">
                                {{ $t('Copy code') }}
                            </div>
                        </Button>
                    </div>
                </div>

                <div class="mb-8 w-full rounded-sm bg-indigo-100 p-4 text-center text-sm font-medium text-indigo-800">
                    <p class="mx-auto max-w-2xl">
                        {{ $t('Payment may take up to 3 days to clear.') }}
                    </p>
                </div>
            </div>
            <Resume :order="order" />
        </section>
    </main>
</template>
